import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { ImageDataDb } from '../_shared/data/image-data-db.data';
import { LinkDirective } from '../_shared/directives/link.directive';
import { IChip } from '../_shared/models/chip.model';
import { IDynamicElement } from '../_shared/models/dynamic/dynamic-element.model';
import { BackgroundVariant } from '../_shared/models/hero/hero-variant.enum';
import { MediaImage } from '../_shared/models/media/media-image.model';
import { IProductHeaderV2 } from '../_shared/models/product/product-header-v2.model';
import { BreadcrumbService } from '../_shared/services/breadcrumb.service';
import { ButtonComponent } from '../button/button.component';
import { ChipsComponent } from '../chip/chips.component';
import { ContainerComponent } from '../container/container.component';
import { DisclosureComponent } from '../disclosure/disclosure.component';
import { HeadingComponent } from '../heading/heading.component';
import { HighlightComponent } from '../highlight/highlight.component';
import { RatingComponent } from '../rating/rating.component';
import { TextHtmlComponent } from '../text-html/text-html.component';
import { ProductGalleryComponent } from './product-gallery/product-gallery.component';

@Component({
    selector: 'sirius-product-header-v2',
    templateUrl: './product-header-v2.component.html',
    styleUrls: ['./product-header-v2.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ContainerComponent,
        ProductGalleryComponent,
        RatingComponent,
        HighlightComponent,
        DisclosureComponent,
        TextHtmlComponent,
        ButtonComponent,
        ChipsComponent,
        LinkDirective,
        HeadingComponent,
        TranslateModule,
    ],
})
export class ProductHeaderV2Component implements IDynamicElement<IProductHeaderV2> {
    readonly config = input<IProductHeaderV2 | undefined>(undefined);

    private readonly _breadcrumbService = inject(BreadcrumbService);
    private readonly _destroyRef = inject(DestroyRef);

    protected readonly imageData = ImageDataDb.productHeaderV2.main;
    protected readonly thumbnailImageData = ImageDataDb.productHeaderV2.thumbnail;

    protected readonly buttonPrimary = computed(() => this.config()?.buttonPrimary);
    protected readonly buttonSecondary = computed(() => this.config()?.buttonSecondary);
    protected readonly highlights = computed(() => this.config()?.highlights);
    protected readonly logo = computed(() => this.config()?.logo);
    protected readonly media = computed<MediaImage[]>(() => this.config()?.media ?? []);
    protected readonly ratings = computed(() => this.config()?.ratings ?? []);
    protected readonly seriesLabels = computed<IChip[]>(() => this.config()?.seriesLabels ?? []);
    protected readonly statusLabel = computed(() => this.config()?.statusLabel);
    protected readonly subTitle = computed(() => this.config()?.subTitle ?? '');
    protected readonly textHtml = computed(() => this.config()?.textHtml ?? '');
    protected readonly title = computed(() => this.config()?.title ?? '');

    protected readonly descriptionMaxLines = computed(() => this.config()?.descriptionMaxLines ?? 4);
    protected readonly hasButtons = computed(() => !!this.buttonPrimary() || !!this.buttonSecondary());
    protected readonly hasThumbnails = computed(() => this.media().length > 1);

    constructor() {
        this._breadcrumbService.setVariant(BackgroundVariant.LIGHT);
        this._destroyRef.onDestroy(() => this._breadcrumbService.reset());
    }
}
